import React from "react";
import { LuLock, LuMessageSquare, LuSearchX } from "react-icons/lu";

/**
 * Why the right panel is empty. Callers import these instead of passing raw
 * strings, so a typo is a build-time reference error rather than a silent
 * fallback to DEFAULT.
 *
 *  DEFAULT    – the lead is open but has no messages yet.
 *  NO_ACCESS  – a ?leadId= link returned nothing AND the user is limited to
 *               their own conversations, so the lead being out of reach is the
 *               explanation we can actually stand behind.
 *  NOT_FOUND  – a ?leadId= link returned nothing for an unrestricted user.
 *               Nothing implies a permission problem here (the id may be wrong,
 *               the lead deleted, or in another location), so the copy stays
 *               neutral rather than claiming access was denied.
 *  NO_RESULTS – a search matched no leads. The list query clears `selectedLead`
 *               when the count is 0, so nothing is open — "No conversation yet"
 *               would wrongly suggest an empty thread.
 */
export const EMPTY_CONVERSATION_VARIANT = {
  DEFAULT: "default",
  NO_ACCESS: "noAccess",
  NOT_FOUND: "notFound",
  NO_RESULTS: "noResults",
};

const VARIANTS = {
  [EMPTY_CONVERSATION_VARIANT.DEFAULT]: {
    Icon: LuMessageSquare,
    title: "No conversation yet",
    description:
      "Start engaging with your lead to begin building a meaningful conversation. Messages will appear here once the communication begins.",
  },
  [EMPTY_CONVERSATION_VARIANT.NO_ACCESS]: {
    Icon: LuLock,
    title: "You don't have access to this conversation",
    description: "This lead is assigned to another user.",
  },
  [EMPTY_CONVERSATION_VARIANT.NOT_FOUND]: {
    Icon: LuSearchX,
    title: "Conversation not available",
    description:
      "This lead couldn't be found. It may have been removed or moved to another location.",
  },
  [EMPTY_CONVERSATION_VARIANT.NO_RESULTS]: {
    Icon: LuSearchX,
    title: "No matching conversation",
    description: "No lead matched your search. Try a different name, phone number or email.",
  },
};

/**
 * Right-panel placeholder for the Activity Center.
 *
 * @param {Object} props
 * @param {typeof EMPTY_CONVERSATION_VARIANT[keyof typeof EMPTY_CONVERSATION_VARIANT]} [props.variant]
 */
const EmptyConversationState = ({ variant = EMPTY_CONVERSATION_VARIANT.DEFAULT }) => {
  const { Icon, title, description } =
    VARIANTS[variant] || VARIANTS[EMPTY_CONVERSATION_VARIANT.DEFAULT];

  return (
    <div className="flex-col-centered items-center justify-center py-16 px-4">
      <div className="flex-col-centered items-center gap-3 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex-row-centered items-center justify-center">
          <Icon size={24} className="text-twLinkColor" strokeWidth={1.75} />
        </div>
        <div className="flex-col-centered items-center gap-1">
          <h3 className="text-base font-[600] text-grey-700 mb-1">{title}</h3>
          <p className="text-sm font-[400] text-grey-500 max-w-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
};

export default EmptyConversationState;
